import React, { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { Modal, Form, Input, Button, Select, Table, Typography, Flex, Tag, Card, Alert, Tooltip } from "antd"
import { ResponsivePage } from "ui/ResponsivePage"
import { Grid } from "antd"
import { DeleteOutlined, StarOutlined, CheckOutlined, SearchOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import ExperienceSelector from "../experience/ExperienceSelector"
import { AiEnhancementModal } from "ui/AiEnhancementModal"
import EquipmentListingMedia from "../UploadEquipmentPhoto"
import { Text } from "ui/Typography"
import LabeledField from "pagesV2/client/Profile/Tabs/ProfileInfo/LabeledField"
import AssetListDetails from "../AssetlistDetails/AssetlistDetails"
import FirstLoading from "modules/loader-watchers/FirstLoading"
import { AlertIcon } from "ui/Icon/DSIcon/AlertIcon"
import { spacings } from "themes/variables"
import { getTrips, getGuideCategoryToBoats, getGuideCharacteristicToBoats, addCategory, selectAddCategoryLoading } from "store/boats"
import { useEquipmentCharacteristics } from "./hooks/useEquipmentCharacteristics"
import { useEquipmentForm } from "./hooks/useEquipmentForm"
import { useEquipmentSubmit } from "./hooks/useEquipmentSubmit"
import styles from "./index.module.css"

const { Title } = Typography
const { Option } = Select

const AddEquipmentModal = ({ visible, onCancel, boatId, record, boatLoading, isDuplicateMode = false, onSubmit }) => {
  const [categorySearch, setCategorySearch] = useState("")
  const [showAddCategory, setShowAddCategory] = useState(false)
  const [newCategoryName, setNewCategoryName] = useState("")
  const [newCategoryError, setNewCategoryError] = useState("")
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const categoryListing = useSelector((state) => state.boats.category_to_boats.items)
  const addingCategory = useSelector(selectAddCategoryLoading)
  const categoryOptions = categoryListing?.map((cat) => ({ label: cat.name, value: String(cat.id) }))
  const characteristicListing = useSelector((state) => state.boats.characteristic_to_boats.items) || []

  // Build a map from record.characteristics as an additional fallback (server may return null names in list API)
  const recordCharacteristicIdToLabel = React.useMemo(() => {
    const map = {}
    if (Array.isArray(record?.characteristics)) {
      for (const c of record.characteristics) {
        if (c?.id == null) continue
        const id = String(c.id)
        if (c.name) map[id] = c.name
      }
    }
    return map
  }, [record?.characteristics])

  // Build a map id -> label (fallback if name null)
  const characteristicIdToLabel = React.useMemo(() => {
    const map = {}
    for (const c of characteristicListing) {
      if (c?.id == null) continue
      const id = String(c.id)
      // Priority: list name > record name fallback > generated fallback
      map[id] = c.name || recordCharacteristicIdToLabel[id] || `Characteristic ${c.id}`
    }
    // Also include any record-only characteristics not present in listing
    for (const id of Object.keys(recordCharacteristicIdToLabel)) {
      if (!map[id]) map[id] = recordCharacteristicIdToLabel[id]
    }
    return map
  }, [characteristicListing, recordCharacteristicIdToLabel])

  // Initialize characteristics hook
  const characteristicsHook = useEquipmentCharacteristics(characteristicListing, characteristicIdToLabel)

  // Initialize form hook
  const formHook = useEquipmentForm(record, boatLoading, boatId, isDuplicateMode, () => categoryName, characteristicsHook)

  // Get category info (after form is created)
  const categoryId = Form.useWatch("category", formHook.form)
  const categoryName = categoryOptions?.find((cat) => cat.value === String(categoryId))?.label || ""

  // Initialize submit hook
  const submitHook = useEquipmentSubmit(formHook, characteristicsHook, record, isDuplicateMode, onCancel, onSubmit)

  const resetInlineAddCategory = () => {
    setShowAddCategory(false)
    setNewCategoryName("")
    setNewCategoryError("")
  }

  const handleAddCategory = async () => {
    const trimmed = newCategoryName.trim()
    if (!trimmed) {
      setNewCategoryError(t("equipment.please_enter_category_name"))
      return
    }
    try {
      const result = await dispatch(addCategory({ name: trimmed }))
      dispatch(getGuideCategoryToBoats()) // background refresh for full sync
      if (result?.id) {
        formHook.form.setFieldsValue({ category: String(result.id) })
      }
      resetInlineAddCategory()
    } catch (error) {
      const errorData = error?.data
      const msg =
        (Array.isArray(errorData?.errors) && errorData.errors[0]) ||
        errorData?.message ||
        errorData?.error ||
        t("equipment.failed_to_add_category", { defaultValue: "Failed to add category" })
      setNewCategoryError(msg)
    }
  }

  // Guide category to boats state
  useEffect(() => {
    dispatch(getGuideCategoryToBoats())
    dispatch(getTrips())
    dispatch(getGuideCharacteristicToBoats())
  }, [dispatch])

  // Trigger form population when modal becomes visible
  useEffect(() => {
    if (visible && record?.loaded && !boatLoading) {
      // Form population is handled inside useEquipmentForm hook
    }
  }, [visible, record?.version, boatLoading, record?.loaded, boatId])

  const columns = [
    {
      title: "Experience",
      dataIndex: "name",
      key: "name",
      render: (name) => <Text fw="500">{name}</Text>
    },
    {
      title: "Guests",
      dataIndex: "guests",
      key: "guests",
      align: "left",
      render: (guests) => <Text>{guests}</Text>
    },
    {
      title: "Price",
      key: "price",
      align: "right",
      render: (_, record) => (
        <Text fw="400">
          {record.currency}
          {record.price.toFixed(2)}
        </Text>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "left",
      render: (status) => {
        const isPublished = String(status).toLowerCase() === "published"
        const displayText = String(status).charAt(0).toUpperCase() + String(status).slice(1)
        return (
          <Tag color={isPublished ? "green" : "default"} icon={isPublished ? <CheckOutlined /> : null}>
            {displayText}
          </Tag>
        )
      }
    },
    {
      title: "",
      key: "action",
      align: "right",
      render: (_, record) => (
        <Button
          type="text"
          icon={<DeleteOutlined className={styles.deleteIcon} color="#525455" />}
          onClick={() => formHook.handleRemoveExperience(record.id)}
          className={styles.removeExperienceButton}
          aria-label="Remove experience"
        />
      )
    }
  ]

  let modalTitle
  if (isDuplicateMode) {
    modalTitle = t("equipment.duplicate_equipment", { defaultValue: "Duplicate Equipment" })
  } else if (record?.id) {
    modalTitle = t("equipment.edit_equipment", { defaultValue: "Edit Equipment" })
  } else {
    modalTitle = t("equipment.add_equipment")
  }

  let submitButtonText
  if (isDuplicateMode) {
    submitButtonText = t("common.create", { defaultValue: "Create" })
  } else if (record?.id) {
    submitButtonText = t("common.update", { defaultValue: "Save Changes" })
  } else {
    submitButtonText = t("common.save")
  }

  const { useBreakpoint } = Grid
  const screens = useBreakpoint()
  const isMobile = !screens.md

  const modalContent = (
    <FirstLoading loading={boatLoading}>
      <Form
        initialValues={{ name: "", category: undefined, max_users: 1, description: "" }}
        form={formHook.form}
        onFinish={submitHook.handleSubmit}
        layout="vertical"
        className={styles.equipmentForm}
      >
        <Flex vertical gap={spacings.S} className={styles.sectionsWrapper}>
          {!isDuplicateMode && (
            <Alert
              className={styles.infoText}
              showIcon
              icon={<AlertIcon />}
              type="info"
              description="Saving changes will affect all listings that use this pool."
            />
          )}

          <Card className={styles.assignExperiencesSection}>
            <Text size="lg" variant="heading" fw="600" className={styles.sectionTitle}>
              {t("equipment.equipment_photo")}
            </Text>
            <Form.Item label={t("boat.images")} className={`${styles.equipmentListingMedia} mt-4 mb-0`}>
              <EquipmentListingMedia
                filteredMedia={formHook.filteredMedia}
                setFilteredMedia={formHook.setFilteredMedia}
                localMedia={formHook.localMedia}
                setLocalMedia={formHook.setLocalMedia}
              />
            </Form.Item>
          </Card>

          <Card className={styles.assignExperiencesSection}>
            <Title level={5} className={`${styles.sectionTitle} mb-4`}>
              {t("equipment.equipment_information")}
            </Title>
            <LabeledField name="name" labelKey={t("equipment.title")} rules={[{ required: true, message: t("equipment.title_required") }]}>
              <Input placeholder={t("equipment.title_placeholder")} />
            </LabeledField>
            <LabeledField
              labelKey={t("equipment.category")}
              name="category"
              rules={[{ required: true, message: t("equipment.category_required") }]}
            >
              <Select
                allowClear
                showSearch={false}
                placeholder={t("equipment.select_category")}
                defaultValue={categoryId}
                className={styles.categorySelect}
                onDropdownVisibleChange={(open) => {
                  if (!open) resetInlineAddCategory()
                }}
                dropdownRender={(menu) => (
                  <>
                    <div className={styles.searchCategory}>
                      <Input
                        placeholder={t("equipment.search_category")}
                        value={categorySearch}
                        onChange={(e) => setCategorySearch(e.target.value)}
                        prefix={<SearchOutlined className={styles.categorySearchIcon} />}
                        allowClear
                        className={styles.categorySearchInput}
                      />
                    </div>
                    <div className={styles.inlineAddCategory}>
                      {!showAddCategory ? (
                        <Button type="link" onClick={() => setShowAddCategory(true)} className={styles.addCategoryLink}>
                          + {t("equipment.add_category")}
                        </Button>
                      ) : (
                        <Flex vertical gap={8}>
                          <Input
                            autoFocus
                            placeholder={t("equipment.category_name")}
                            value={newCategoryName}
                            status={newCategoryError ? "error" : ""}
                            onChange={(e) => {
                              setNewCategoryName(e.target.value)
                              if (newCategoryError) setNewCategoryError("")
                            }}
                            onPressEnter={handleAddCategory}
                          />
                          {newCategoryError && <div className={styles.addCategoryError}>{newCategoryError}</div>}
                          <Flex gap={8} className={styles.addCategoryActions}>
                            <Button onClick={resetInlineAddCategory}>{t("equipment.cancel")}</Button>
                            <Button type="primary" loading={addingCategory} onClick={handleAddCategory}>
                              {t("equipment.save")}
                            </Button>
                          </Flex>
                        </Flex>
                      )}
                    </div>
                    {menu}
                  </>
                )}
                optionFilterProp="children"
                filterOption={false}
                onChange={(value) => {
                  if (value === undefined) {
                    formHook.form.setFieldsValue({ category: undefined })
                    return
                  }
                  formHook.form.setFieldsValue({ category: value })
                }}
              >
                {Array.isArray(categoryOptions) &&
                  categoryOptions.length > 0 &&
                  categoryOptions
                    .filter((cat) => !categorySearch || cat.label.toLowerCase().includes(categorySearch.toLowerCase()))
                    .map((cat) => (
                      <Option className={styles.categoryOption} key={cat.value} value={cat.value}>
                        {cat.label}
                      </Option>
                    ))}
              </Select>
            </LabeledField>

            <LabeledField
              name="max_users"
              labelKey={t("equipment.max_users")}
              rules={[{ required: true, message: t("equipment.max_users_required") }]}
            >
              <Input
                type="number"
                min={1}
                className="w-100 input-users"
                onInput={(e) => {
                  const value = e.target.value
                  if (value === "" || Number.isNaN(value)) return
                  const intValue = Math.max(1, Number.parseInt(value, 10) || 1)
                  e.target.value = intValue
                }}
              />
            </LabeledField>
            <Flex justify="space-between" align="center" className="mb-xxs" gap={spacings.XS}>
              <Text fw="500">{t("equipment.description")}</Text>
              <Tooltip title={formHook.aiDescriptionData?.length < 25 ? t("marketing.ai_enable_text_generation") : ""} placement="top">
                <span>
                  <Button
                    type="primary"
                    icon={<StarOutlined />}
                    onClick={() => formHook.setShowAiModal(true)}
                    disabled={!formHook.aiDescriptionData || formHook.aiDescriptionData.length < 25}
                    style={!formHook.aiDescriptionData || formHook.aiDescriptionData.length < 25 ? { pointerEvents: "not-allowed" } : {}}
                  >
                    {t("pro.ai_enhancement.button_text")}
                  </Button>
                </span>
              </Tooltip>
            </Flex>
            <LabeledField name="description" noStyle rules={[{ required: true, message: t("equipment.description_required") }]}>
              <Input.TextArea
                rows={5}
                placeholder={t("equipment.description_placeholder")}
                value={formHook.aiDescriptionData}
                onChange={(e) => {
                  const value = e.target.value
                  formHook.setAiDescriptionData(value)
                  formHook.form.setFieldsValue({ description: value })
                }}
              />
            </LabeledField>
            {formHook.showAiModal && (
              <AiEnhancementModal
                className="ai-enhancement-modal"
                open={formHook.showAiModal}
                description={formHook.aiDescriptionData}
                setDescription={(desc) => {
                  formHook.setAiDescriptionData(desc)
                  formHook.form.setFieldsValue({ description: desc })
                }}
                profileDescription={formHook.profileDescription}
                setProfileDescription={formHook.setProfileDescription}
                onOk={() => {
                  formHook.setShowAiModal(false)
                }}
                onCancel={() => {
                  formHook.setShowAiModal(false)
                  formHook.setProfileDescription("")
                }}
                text="Equipment Description"
                modalTitle={t("pro.ai_enhancement.modal_title")}
                messages={t("pro.ai_enhancement.form.continue_refinement_info")}
                refinementTooltipTitle={t("pro.ai_enhancement.refinement_tooltip")}
                improveWithAITooltipTitle={t("pro.ai_enhancement.improve_with_ai_tooltip")}
              />
            )}

            <div className="characteristics-section my-6 mb-0">
              <LabeledField name="characteristics" labelKey={t("equipment.add_characteristics")}>
                <Select
                  mode="tags"
                  showSearch
                  allowClear
                  placeholder="Select characteristics"
                  value={characteristicsHook.selectedIds}
                  onChange={(ids, options) => characteristicsHook.handleCharacteristicsChange(ids, options, formHook.form)}
                  className={styles.characteristicsSelect}
                  options={characteristicsHook.characteristicOptions}
                  optionLabelProp="label"
                  optionFilterProp="label"
                  tagRender={characteristicsHook.renderCharacteristicTag}
                  filterOption={(input, option) => (option?.label || "").toLowerCase().includes(input.toLowerCase())}
                />
              </LabeledField>
            </div>
          </Card>

          <Card className={styles.assignExperiencesSection}>
            <Title level={5} className={`${styles.sectionTitle} mb-5`}>
              {t("equipment.modals.assign_experiences")}{" "}
              <span className={styles.experiencesCount}>({formHook.selectedExperienceData.length})</span>
            </Title>
            <LabeledField
              name="experiences"
              labelKey={t("equipment.experiences")}
              className="mb-0"
              rules={[{ required: true, message: t("equipment.experience_required") }]}
            >
              <ExperienceSelector
                experiences={formHook.experiencesMemo}
                value={formHook.selectedExperiences}
                onChange={(vals) => {
                  const strVals = vals.map(String)
                  // Avoid redundant updates: only set if actually changed
                  if (
                    strVals.length !== formHook.selectedExperiences.length ||
                    strVals.some((v, i) => v !== formHook.selectedExperiences[i])
                  ) {
                    formHook.setSelectedExperiences(strVals)
                    formHook.form.setFieldsValue({ experiences: strVals })
                  }
                }}
              />
              {formHook.selectedExperienceData.length === 0 && (
                <Text size="caption" className={styles.emptyExperiencesText}>
                  {t("equipment.no_experience_assigned", { defaultValue: "No experience assigned to this equipment." })}
                </Text>
              )}
              {formHook.selectedExperienceData.length > 0 && (
                <div>
                  <Title level={5} className={`mt-5 ${styles.selectedExperiencesTitle}`}>
                    {t("equipment.assigned_to_experiences")}
                  </Title>
                  <Table
                    dataSource={formHook.selectedExperienceData}
                    className={styles.selectedExperienceData}
                    rowKey="id"
                    pagination={false}
                    columns={columns}
                  />
                </div>
              )}
            </LabeledField>
          </Card>

          <Card className={styles.assignExperiencesSection}>
            <AssetListDetails
              form={formHook.form}
              categoryName={categoryName}
              assets={formHook.assets}
              setAssets={formHook.setAssets}
              equipmentId={record?.id}
            />
          </Card>
        </Flex>
        <Flex gap={spacings.S} className={styles.footerActions}>
          <Button variant="outlined" onClick={onCancel} color="primary" className={styles.footerCancelBtn}>
            {t("common.cancel")}
          </Button>
          <Button variant="solid" htmlType="submit" color="primary" loading={submitHook.isLoading} className={styles.footerSaveBtn}>
            {submitButtonText}
          </Button>
        </Flex>
      </Form>
    </FirstLoading>
  )

  return isMobile ? (
    <ResponsivePage onBackClick={onCancel} pageTitle={modalTitle}>
      {modalContent}
    </ResponsivePage>
  ) : (
    <Modal
      title={modalTitle}
      open={visible}
      onCancel={onCancel}
      className={styles.addEquipmentModal}
      width={800}
      footer={null}
      maskClosable={false}
    >
      {modalContent}
    </Modal>
  )
}

AddEquipmentModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  record: PropTypes.object,
  boatId: PropTypes.any,
  boatLoading: PropTypes.bool,
  isDuplicateMode: PropTypes.bool,
  onSubmit: PropTypes.func
}

export default AddEquipmentModal
